/**
 * Event vocabulary + param builders.
 *
 * GA4 standard event names are used where they exist (login, sign_up,
 * begin_checkout, purchase, search, select_content, add_to_wishlist) so GA4
 * reports and audiences work out of the box. The pixel receives the same
 * names with richer params.
 */

import { HEARTBEAT_SAMPLE_RATE } from './config';

export const EVENTS = {
  // Lifecycle / session
  APP_OPEN: 'app_open', // app/site launch — the session-open event on EVERY platform
  //   (web is an app too). Fires once per session; a new device/first-ever open
  //   is `app_open` with session_number = 1 (no separate first_visit event).
  FIRST_VISIT: 'first_visit', // (legacy) superseded by app_open + session_number
  SESSION_START: 'session_start', // (legacy) superseded by app_open
  // Auth
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGN_UP: 'sign_up',
  // Subscription funnel
  BEGIN_CHECKOUT: 'begin_checkout',
  PURCHASE: 'purchase',
  SUBSCRIBE: 'subscribe',
  // Content engagement
  SELECT_CONTENT: 'select_content',
  SEARCH: 'search',
  ADD_TO_WATCHLIST: 'add_to_watchlist',
  REMOVE_FROM_WATCHLIST: 'remove_from_watchlist',
  ADD_TO_FAVORITES: 'add_to_favorites',
  REMOVE_FROM_FAVORITES: 'remove_from_favorites',
  FIGHTER_PROFILE_VIEW: 'fighter_profile_view',
  FOLLOW_FIGHTER: 'follow_fighter',
  UNFOLLOW_FIGHTER: 'unfollow_fighter',
  // Video
  VIDEO_VIEW: 'video_view', // watch page opened (pixel-only; GA4 keeps page_view)
  VIDEO_START: 'video_start',
  VIDEO_PROGRESS: 'video_progress',
  VIDEO_COMPLETE: 'video_complete',
  VIDEO_SEEK: 'video_seek', // real (native seeking/seeked) when player-bound; else derived
  VIDEO_REBUFFER: 'video_rebuffer', // real (native waiting→playing) when player-bound; else derived
  VIDEO_PAUSE: 'video_pause',
  VIDEO_ERROR: 'video_error',
  VIDEO_HEARTBEAT: 'video_heartbeat', // periodic watch-time ping
  // Video quality of experience (real, from native HTMLMediaElement signals)
  VIDEO_STARTUP: 'video_startup', // time from mount to first frame (startup_ms)
  VIDEO_QUALITY_CHANGE: 'video_quality_change', // rendition change (resolution / bitrate_kbps)
};

/**
 * Per-event sink + sampling policy — the single source of truth for "what goes
 * where." Mirrors how streaming platforms split product analytics (GA4: a small
 * set of business KPIs) from the operational telemetry firehose (the pixel →
 * BigQuery). Only events that deviate from the default are listed; anything
 * absent fans out to BOTH sinks with no sampling.
 *
 *   ga / pixel : default sink routing (a call-site `{ ga, pixel }` still wins)
 *   sample     : fraction in [0,1] of sessions that emit this event
 *                (deterministic per session_id); omitted = always emit
 *
 * GA4 keeps only the KPI set: page_view + session_start/first_visit (native),
 * auth, the subscription funnel, discovery/fighters, and the two video KPIs
 * `video_start` + `video_complete` (plus `video_error` as a health signal).
 * Every high-frequency / operational video event is pixel-only.
 */
export const EVENT_POLICY = {
  // Session-open lifecycle → pixel firehose only (GA4 has its own native
  // session_start / first_open, and app_open isn't a GA4 web event).
  [EVENTS.APP_OPEN]: { ga: false },
  [EVENTS.FIRST_VISIT]: { ga: false }, // legacy
  [EVENTS.SESSION_START]: { ga: false }, // legacy
  // Watch route keeps GA4's page_view; the pixel gets video_view instead.
  [EVENTS.VIDEO_VIEW]: { ga: false },
  // Periodic liveness ping — pixel-only + the sampling escape hatch.
  [EVENTS.VIDEO_HEARTBEAT]: { ga: false, sample: HEARTBEAT_SAMPLE_RATE },
  // Operational / high-frequency video telemetry → pixel (BigQuery) only.
  [EVENTS.VIDEO_PROGRESS]: { ga: false },
  [EVENTS.VIDEO_SEEK]: { ga: false },
  [EVENTS.VIDEO_PAUSE]: { ga: false },
  [EVENTS.VIDEO_REBUFFER]: { ga: false },
  [EVENTS.VIDEO_STARTUP]: { ga: false },
  [EVENTS.VIDEO_QUALITY_CHANGE]: { ga: false },
};

/**
 * Deterministic string → [0, 1) hash (FNV-1a variant). Used to sample events
 * consistently per session: the same session_id always maps to the same value,
 * so a sampled-out session drops every one of its heartbeats (clean ×N
 * concurrency weighting) rather than flickering in and out.
 * @param {string} str
 * @returns {number} in [0, 1)
 */
export const hashToUnit = (str = '') => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // >>> 0 → unsigned; divide by 2^32 for a stable fraction in [0, 1).
  return (h >>> 0) / 0x100000000;
};

/**
 * Normalize a content item into the shared content context attached to every
 * video / content event. Tolerant of the various shapes across DSP/CMS data.
 * @param {Object} item
 * @returns {Object}
 */
export const contentContext = (item = {}) => {
  const clean = {
    content_id: item.content_id || item.videoId || item.id || item._id,
    content_title: item.content_title || item.title || item.name,
    content_type: item.content_type || item.type, // live | movie | series | fighter | ppv
    series_id: item.series_id || item.seriesId,
    event_name: item.event_name || item.event,
    entitlement: item.entitlement, // free | subscriber | ppv
  };
  // Drop undefined keys so the pixel payload stays lean.
  Object.keys(clean).forEach(k => clean[k] === undefined && delete clean[k]);
  return clean;
};

/**
 * Normalize a fighter record for fighter events.
 * @param {Object} fighter
 * @returns {Object}
 */
export const fighterContext = (fighter = {}) => {
  const clean = {
    fighter_id: fighter.fighter_id || fighter.id || fighter._id,
    fighter_name:
      fighter.fighter_name ||
      fighter.name ||
      [fighter.firstName, fighter.lastName].filter(Boolean).join(' ') ||
      undefined,
    weight_class: fighter.weight_class || fighter.weightClass || fighter.division,
  };
  Object.keys(clean).forEach(k => clean[k] === undefined && delete clean[k]);
  return clean;
};
